import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAvailableForms, useForms } from '@features/forms/store';
import { useStripeConnect } from '@features/payments';
import { useSubjectLanguageVariants } from '@features/user';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { useBookingGates } from '../store/hooks';
import type { Checkpoint, GateKind, PatientApplicability } from '../types';

import {
  BookingGatesFunnelView,
  type FunnelGate,
} from './BookingGatesFunnelView';

/** Gate kinds the funnel lets you place. `fit` is a coming-soon teaser and
 * `eligibility` drives the coverage toggle, so neither is a placeable row. */
const PLACEABLE_KINDS: GateKind[] = ['card', 'paperwork'];

/** Checkpoint stored for the enrichment eligibility gate. Enrichment never
 * blocks, so the checkpoint is inert — we pick the eligibility default. */
const ELIGIBILITY_CHECKPOINT: Checkpoint = 'before_calendar';

/** Container: maps the funnel model onto the gate API. Placing a gate creates
 * it at that checkpoint, moving patches the checkpoint, removing deletes it,
 * and the coverage toggle creates/deletes an enrichment eligibility gate. */
export function BookingGatesFunnelSection() {
  // The org's assignable forms feed the paperwork gate's intake-form picker.
  // Two catalogs exist (GDN-2443): the legacy v1 list (registered
  // form_definitions + public forms) and v2 form-builder forms. The funnel
  // backend accepts ids from either, so the picker merges both.
  const { data: availableForms } = useAvailableForms({ limit: 100 });
  const { forms: builderForms } = useForms({ pageSize: 100 });
  const intakeFormOptions = useMemo(() => {
    const byId = new Map<string, { form_id: string; title: string }>();
    for (const form of availableForms) {
      byId.set(form.form_id, { form_id: form.form_id, title: form.title });
    }
    for (const item of builderForms) {
      const formId = item.form.id;
      if (!formId) continue;
      byId.set(formId, {
        form_id: formId,
        title: item.current_revision?.title || 'Untitled form',
      });
    }
    return [...byId.values()].sort((a, b) => a.title.localeCompare(b.title));
  }, [availableForms, builderForms]);
  const { gatesByKind, isLoading, error, addGate, updateGateById, removeGate } =
    useBookingGates();
  const { plural } = useSubjectLanguageVariants();
  const { enqueueSnackbar } = useSnackbar();
  const { schedulingSettings, error: errorTracking } = useEventTracking();
  const navigate = useNavigate();
  const { isStripeEnabled, isLoading: isStripeStatusLoading } =
    useStripeConnect();
  // A card gate is inert until the practice can actually take payments. Treat
  // the status as ready while it's still loading so the warning doesn't flash.
  const stripePaymentsReady = isStripeStatusLoading || Boolean(isStripeEnabled);

  const handleSetupPayments = useCallback(() => {
    schedulingSettings.setupPaymentsClicked();
    navigate('/practice/billing-settings?tab=patient-payments');
  }, [schedulingSettings, navigate]);

  const run = useCallback(
    (fn: () => Promise<unknown>, failureMessage: string, errorKey: string) => {
      void (async () => {
        try {
          await fn();
        } catch {
          enqueueSnackbar(failureMessage, { variant: 'error' });
          errorTracking.track(failureMessage, errorKey);
        }
      })();
    },
    [enqueueSnackbar, errorTracking],
  );

  const gates: FunnelGate[] = PLACEABLE_KINDS.flatMap(kind => {
    const gate = gatesByKind[kind];
    return gate
      ? [
          {
            kind,
            checkpoint: gate.checkpoint,
            appliesTo: gate.applies_to_patient,
            formDefinitionId: gate.form_definition_id ?? null,
          },
        ]
      : [];
  });
  const showCoverage = Boolean(gatesByKind.eligibility);

  const handleAddGate = useCallback(
    (kind: GateKind, checkpoint: Checkpoint) =>
      run(
        async () => {
          await addGate(kind, checkpoint);
          schedulingSettings.gateToggled({ gate: kind, enabled: true });
        },
        'Couldn’t add that check',
        'scheduling_gate_add',
      ),
    [run, addGate, schedulingSettings],
  );

  const handleMoveGate = useCallback(
    (kind: GateKind, checkpoint: Checkpoint) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await updateGateById(gate.policy_id, { checkpoint });
          schedulingSettings.gateChanged({ gate: kind, field: 'checkpoint' });
        },
        'Couldn’t move that check',
        'scheduling_gate_move',
      );
    },
    [run, gatesByKind, updateGateById, schedulingSettings],
  );

  const handleRemoveGate = useCallback(
    (kind: GateKind) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await removeGate(gate.policy_id);
          schedulingSettings.gateToggled({ gate: kind, enabled: false });
        },
        'Couldn’t remove that check',
        'scheduling_gate_remove',
      );
    },
    [run, gatesByKind, removeGate, schedulingSettings],
  );

  const handleChangeAppliesTo = useCallback(
    (kind: GateKind, value: PatientApplicability) => {
      const gate = gatesByKind[kind];
      if (!gate) return;
      run(
        async () => {
          await updateGateById(gate.policy_id, { applies_to_patient: value });
          schedulingSettings.gateChanged({
            gate: kind,
            field: 'applies_to_patient',
          });
        },
        'Couldn’t update who this applies to',
        'scheduling_gate_applies_to',
      );
    },
    [run, gatesByKind, updateGateById, schedulingSettings],
  );

  const handleToggleCoverage = useCallback(
    (enabled: boolean) => {
      const existing = gatesByKind.eligibility;
      run(
        async () => {
          if (enabled && !existing) {
            await addGate('eligibility', ELIGIBILITY_CHECKPOINT, {
              mode: 'enrichment',
            });
          } else if (!enabled && existing) {
            await removeGate(existing.policy_id);
          }
          schedulingSettings.gateToggled({ gate: 'eligibility', enabled });
        },
        'Couldn’t update coverage display',
        'scheduling_gate_coverage',
      );
    },
    [run, gatesByKind, addGate, removeGate, schedulingSettings],
  );

  const handleChangeIntakeForm = useCallback(
    (kind: GateKind, formDefinitionId: string | null) => {
      run(
        async () => {
          const gate = gatesByKind[kind];
          if (!gate) return;
          await updateGateById(gate.policy_id, {
            form_definition_id: formDefinitionId,
          });
          schedulingSettings.gateChanged({ gate: kind, field: 'form_definition_id' });
        },
        'Couldn\u2019t update the intake form',
        'scheduling_gate_intake_form',
      );
    },
    [run, gatesByKind, updateGateById, schedulingSettings],
  );

  return (
    <BookingGatesFunnelView
      gates={gates}
      showCoverage={showCoverage}
      subjectPlural={plural}
      isLoading={isLoading}
      hasError={Boolean(error)}
      onAddGate={handleAddGate}
      onMoveGate={handleMoveGate}
      onRemoveGate={handleRemoveGate}
      onChangeAppliesTo={handleChangeAppliesTo}
      intakeFormOptions={intakeFormOptions}
      onChangeIntakeForm={handleChangeIntakeForm}
      onToggleCoverage={handleToggleCoverage}
      stripePaymentsReady={stripePaymentsReady}
      onSetupPayments={handleSetupPayments}
    />
  );
}
